import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Flame, Star, User, ChevronLeft, Copy, Check, GitBranch } from "lucide-react";
import Navbar from "../components/Navbar";
import GameCanvas from "../game/GameCanvas";
import api from "../lib/api";

export default function CommunityLevel() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [level, setLevel] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [rateBusy, setRateBusy] = useState(false);
  const [rateErr, setRateErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/community/level/${code}`);
      setLevel(r.data);
    } catch (_) {
      setLevel(false);
    }
  }, [code]);

  useEffect(() => { refresh(); }, [refresh]);

  const playableLevel = useMemo(() => {
    if (!level) return null;
    return {
      ballSpeed: level.ball_speed,
      grid: level.grid,
      name: level.name,
      seed: `community-${level.code}`,
    };
  }, [level]);

  const startPlay = async () => {
    // Fire-and-forget play counter; don't block UX
    api.post(`/community/level/${code}/played`).catch(() => {});
    setPlaying(true);
  };

  const rate = async (stars) => {
    if (rateBusy) return;
    setRateBusy(true); setRateErr(null);
    try {
      const r = await api.post(`/community/level/${code}/rate`, { stars });
      setLevel(r.data);
    } catch (e) {
      setRateErr(e.response?.data?.detail || "Rating failed.");
    } finally {
      setRateBusy(false);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/community/${code}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) { /* noop */ }
  };

  if (level === null) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" /> <Navbar />
        <main className="relative z-10 flex items-center justify-center min-h-screen font-mono-cosmic text-[#8b949e] tracking-widest">
          LOADING LEVEL...
        </main>
      </div>
    );
  }

  if (level === false) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" /> <Navbar />
        <main className="relative z-10 max-w-3xl mx-auto px-4 pt-28 pb-16 text-center">
          <div className="font-mono-cosmic text-[11px] tracking-[0.3em] text-[#ff007f] uppercase mb-2">
            // 404
          </div>
          <h1 className="font-heading font-black uppercase text-3xl tracking-tighter mb-3">
            Level not found
          </h1>
          <p className="text-white/60 mb-6">
            This share code doesn't exist — or was deleted by the author.
          </p>
          <Link to="/community" className="btn-neon" data-testid="community-404-back">
            Back to Community
          </Link>
        </main>
      </div>
    );
  }

  if (playing && playableLevel) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" /> <Navbar />
        <main className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-16 fade-in">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#00ffff] uppercase">
                // COMMUNITY · {code}
              </div>
              <div className="font-heading font-black uppercase text-2xl tracking-tighter text-white">
                {level.name}
              </div>
            </div>
            <button
              onClick={() => { setPlaying(false); refresh(); }}
              className="btn-neon"
              data-testid="community-play-exit"
            >
              Exit
            </button>
          </div>
          <GameCanvas
            key={`community-${code}`}
            level={1}
            levelOverride={playableLevel}
            onComplete={() => { setPlaying(false); refresh(); }}
            onExit={() => { setPlaying(false); refresh(); }}
            bestScore={0}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="starfield" /> <Navbar />
      <main className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-16 fade-in">
        <button
          onClick={() => navigate("/community")}
          className="inline-flex items-center gap-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] text-white/60 hover:text-white uppercase mb-4"
          data-testid="community-back"
        >
          <ChevronLeft className="w-3 h-3" /> Community
        </button>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <div className="glass p-5 border border-white/15 min-w-0" data-testid="community-level-panel">
            <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#00ffff] uppercase mb-2">
              // SHARE CODE · {level.code}
            </div>
            <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter mb-2">
              {level.name}
            </h1>
            {level.remix_of && (
              <Link
                to={`/community/${level.remix_of}`}
                className="inline-flex items-center gap-1.5 mb-3 px-2 py-1 border border-[#b026ff]/50 text-[#b026ff] hover:bg-[#b026ff]/10 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase transition"
                data-testid="community-remix-of"
              >
                <GitBranch className="w-3 h-3" />
                {level.remix_depth > 1
                  ? `Remix gen ${level.remix_depth}`
                  : "Remix"} of {level.remix_of_name || level.remix_of}
                {level.remix_of_author && ` · by ${level.remix_of_author}`}
              </Link>
            )}
            {level.description && (
              <p className="text-white/60 text-sm mb-4">{level.description}</p>
            )}
            <div className="flex items-center gap-3 mb-4 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase">
              <span className="inline-flex items-center gap-1.5 text-white/60">
                <User className="w-3 h-3" /> {level.author_name}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#ff8c1a]">
                <Flame className="w-3 h-3" /> {level.plays} plays
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#ffd700]">
                <Star className="w-3 h-3" />
                {level.stars_count > 0 ? `${level.stars_avg.toFixed(1)} (${level.stars_count})` : "unrated"}
              </span>
            </div>

            {/* Mini preview */}
            <div
              className="border border-white/10 grid gap-[1px] p-1 bg-black/40 mb-4 mx-auto"
              style={{
                gridTemplateColumns: `repeat(${level.grid[0]?.length || 12}, 1fr)`,
                maxWidth: "460px",
              }}
              data-testid="community-level-preview"
            >
              {level.grid.map((row, r) =>
                row.map((v, c) => {
                  const bg =
                    v === 1 ? "#00ffff" :
                    v === 2 ? "#ffd700" :
                    v === 3 ? "#ff007f" :
                    v === 4 ? "#b026ff" : "transparent";
                  return (
                    <div
                      key={`${r}-${c}`}
                      className="aspect-square"
                      style={{ background: bg }}
                    />
                  );
                })
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                onClick={startPlay}
                className="btn-neon cyan"
                data-testid="community-play-btn"
              >
                Play Level
              </button>
              <button
                onClick={() => navigate(`/builder?remix=${code}`)}
                className="py-2 border border-[#b026ff] text-[#b026ff] hover:bg-[#b026ff]/10 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition inline-flex items-center justify-center gap-2"
                data-testid="community-remix-btn"
              >
                <GitBranch className="w-3.5 h-3.5" /> Remix
              </button>
              <button
                onClick={copyShare}
                className="py-2 border border-white/20 hover:border-[#00ffff] hover:text-[#00ffff] text-white/70 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition inline-flex items-center justify-center gap-2"
                data-testid="community-share-btn"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy Link"}
              </button>
            </div>

            <div className="mt-3 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase text-white/40">
              by{" "}
              <button
                onClick={() => navigate(`/u/${level.author_id}`)}
                className="text-white/70 hover:text-[#00ffff] transition"
                data-testid="community-author-link"
              >
                {level.author_name}
              </button>
            </div>
          </div>

          <aside className="glass p-5 border border-white/15 self-start" data-testid="community-rate-panel">
            <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-[#ffd700] uppercase mb-2">
              // RATE THIS LEVEL
            </div>
            <div className="font-heading uppercase text-lg tracking-tight text-white mb-3">
              How was it?
            </div>
            <div className="flex items-center gap-1.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  onClick={() => rate(s)}
                  disabled={rateBusy}
                  className="w-10 h-10 border border-white/15 hover:border-[#ffd700] hover:text-[#ffd700] text-white/60 flex items-center justify-center transition"
                  data-testid={`community-rate-${s}`}
                  title={`${s} star${s === 1 ? "" : "s"}`}
                >
                  <Star className="w-5 h-5" fill={s <= Math.round(level.stars_avg) ? "#ffd700" : "none"} />
                </button>
              ))}
            </div>
            <div className="mt-3 font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/40">
              {level.stars_count > 0 ? `avg ${level.stars_avg.toFixed(2)} · ${level.stars_count} ratings` : "Be the first to rate"}
            </div>
            {rateErr && (
              <div className="mt-3 glass p-2 border border-[#ff007f]/60 text-[#ff007f] font-mono-cosmic text-[10px] tracking-widest uppercase" data-testid="community-rate-err">
                {rateErr}
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
